import { HTMLAttributes, ReactNode, SVGProps } from 'react';
import { Placement } from '@floating-ui/react';
import { IDocumentData } from '@models/document/IDocumentData';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { Variant } from '@/types/ColorVariants';

/** Элемент списка для Radio, Checkbox и Select */
export interface IOption {
    label: string;
    value: any;
    disabled?: boolean;
    cssClass?: string;
    node?: ReactNode;
    icon?: ReactNode;
    url?: string;
    payload?: any;
}

export interface ITreeOption extends IOption {
    checked?: boolean;
    hasCheckedChild?: boolean;
    parent?: ITreeOption;
    children?: ITreeOption[];
    variant?: Variant;
    loading?: boolean;
    hasChildren?: boolean;
}

/** Таб */
export interface ITab {
    /** Название вкладки */
    label: ReactNode;
    /** Содержание вкладки */
    tab?: ReactNode;
    /** Доступность вкладки */
    disabled?: boolean;
    /** Активная вкладка */
    active?: boolean;
    /** Ссылка */
    url?: string;
    /** Точность url */
    exact?: boolean;
    /** Иконка вкладки */
    icon?: ReactNode;
    /** Действие при клике на вкладку */
    handler?: (element?: HTMLElement) => void;
}

/** Таб */
export interface ISegment {
    label: ReactNode;
    value: string;
    disabled?: boolean;
    /** Иконка */
    icon?: ReactNode;
}

/** Элемент выпадающего списка */
export interface IListElement {
    /** Компонент элемента */
    label: ReactNode;
    /** Обработчик */
    handler?: () => void;
    /** Ссылка */
    url?: string;
    /** ID значение */
    value?: string;
    /** Disabled */
    disabled?: boolean;
    /** Отделение линией */
    separated?: boolean;
    /** Отделение линией снизу*/
    separatedBottom?: boolean;
    /** Указывает что это заголовок раздела */
    isTitle?: boolean;
    /** Отключает обработку клика по элементу */
    preventClick?: boolean;
    /** Стиль */
    style?: HTMLAttributes<HTMLDivElement>;
    /** Произвольный trailing-контент справа (кнопка/иконка/меню) */
    endAdornment?: React.ReactNode | ((ctx: { item: IListElement; index: number }) => React.ReactNode);
    /** Подсказка при наведении */
    tooltip?: string;
}

/** Контекст табов */
export interface ITabsContext {
    activeTab: number;
}

/** Контекст меню */
export interface IMenuContext {
    onClose: () => void;
    show: boolean;
}

/** Контекст создания документа через модальное окно */
export interface IDocumentModalContext {
    data?: IDocumentData;
    scheme?: IDocumentScheme;
}

/** Результат обработки файла */
export interface IFileData {
    file: File;
    base64: string;
    base64Compressed?: string;
    id?: string;
}

/** Форматированная дата */
export interface IFormattedDate {
    month: string;
    monthLong: string;
    monthShort: string;
    monthName: string;
    dayOfMonth: string;
    dayOfWeek: string;
    hour: string;
    minutes: string;
    year: number;
    date: string;
    time: string;
}

/** Цвет фона аватара */
export type AvatarColor = 'default' | 'white' | 'grey' | 'black';

/** Размер */
export type Size = 'xxxxs' | 'xxxs' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'xxxl' | 'xxxxl';

/** Список кнопок */
export interface IButtonGroup {
    /** Кнопка */
    component?: ReactNode;
    /** Подпись в меню */
    label?: ReactNode;
    /** Описание в меню */
    description?: ReactNode;
    /** Текст при наведении */
    tooltip?: ReactNode;
    /** Ссылка в меню */
    url?: string;
    /** Клик по кнопке в меню */
    onClick?: () => void;
    /** неактивное состояние */
    disabled?: boolean;
    /** лоудер */
    preloader?: boolean;
}

/** Пропсы иконки */
export interface IIconProps extends SVGProps<SVGSVGElement> {
    /** размер */
    size?: Size;
}

export type DropdownPosition = Placement;

export enum YearsShowType {
    AllYears,
    CurrentYear,
}
